import {
  type Column,
  ExportApiReportFormatters,
} from '@ovComponents/3-pattern/downloadButton/downloadButton';
import type { EnrichedPortfolioReport } from '../hooks/usePortfolioTableState.types';
import {
  buildPortfolioExportFileName,
  getPortfolioExportColumns,
  type PortfolioExportTranslate,
} from './portfolioExportConfig';

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);
}

function formatCellValue(
  value: unknown,
  formatter?: ExportApiReportFormatters,
): string {
  if (value == null) return '';

  if (formatter === ExportApiReportFormatters.DIVIDE_BY_100) {
    return String(Number(value) / 100);
  }
  if (formatter === ExportApiReportFormatters.MULTIPLY_BY_100) {
    return String(Number(value) * 100);
  }

  return String(value);
}

// Prefix values that Excel/Sheets would interpret as a formula (leading
// =, +, -, or @) with a single quote so they render as plain text and
// can't execute a formula when the exported CSV is opened.
const FORMULA_INJECTION_PREFIX = /^[=+\-@]/;

function escapeCsvField(value: string): string {
  const safeValue = FORMULA_INJECTION_PREFIX.test(value) ? `'${value}` : value;
  if (/[",\n\r]/.test(safeValue)) {
    return `"${safeValue.replace(/"/g, '""')}"`;
  }
  return safeValue;
}

function buildCsvRow(
  report: EnrichedPortfolioReport,
  columns: Column[],
): string {
  return columns
    .map((column) => {
      const raw = getNestedValue(
        report as unknown as Record<string, unknown>,
        column.gqlAlias,
      );
      return escapeCsvField(formatCellValue(raw, column.formatter));
    })
    .join(',');
}

export function downloadSelectedPortfoliosCsv(
  reports: EnrichedPortfolioReport[],
  t: PortfolioExportTranslate,
): void {
  if (reports.length === 0) return;

  const columns = getPortfolioExportColumns(t);
  const header = columns
    .map((column) => escapeCsvField(column.header))
    .join(',');
  const rows = reports.map((report) => buildCsvRow(report, columns));
  const csv = [header, ...rows].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${buildPortfolioExportFileName(t)}-selected.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}
